import { useEffect, useLayoutEffect, useRef } from 'react';
import { lockScroll, unlockScroll } from './scrollLock';

/**
 * Drives a native modal `<dialog>` from an `open` prop and animates it out as
 * well as in. Closing sets `data-closing`, waits for the exit animation from
 * the CSS and only then calls `close()`, so focus returns to the opener.
 *
 * Callers often unmount the dialog instead of passing `open={false}`. React
 * then removes the element at once; an inert copy plays the exit animation in
 * its place.
 *
 * The dialog element needs `tabIndex={-1}` so it can take the initial focus.
 */
export function useModalDialog(open: boolean) {
  const ref = useRef<HTMLDialogElement>(null);
  const opener = useRef<Element | null>(null);
  const counted = useRef(false);
  const locked = useRef(false);

  /** The page behind a modal dialog must not scroll (#153). */
  const lock = (on: boolean) => {
    if (on === locked.current) return;
    locked.current = on;
    if (on) lockScroll();
    else unlockScroll();
  };

  /** On phones the page behind a sheet steps back (CSS reads `data-sheets`). */
  const stepBack = (on: boolean) => {
    if (on === counted.current) return;
    if (on && !window.matchMedia?.('(max-width: 639px)').matches) return;
    counted.current = on;
    const root = document.documentElement;
    const n = Number(root.dataset.sheets ?? 0) + (on ? 1 : -1);
    if (n > 0) root.dataset.sheets = String(n);
    else delete root.dataset.sheets;
  };

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open) {
      delete dialog.dataset.closing;
      stepBack(true);
      if (!dialog.open) {
        // Read the scroll offset before the lock pins the page.
        dialog.style.removeProperty('--sheet-drag');
        dialog.style.removeProperty('--sheet-out-duration');
        document.documentElement.style.setProperty('--sheet-scroll', `${window.scrollY}px`);
        lock(true);
        opener.current = document.activeElement;
        dialog.showModal();
        // `showModal` focuses the first control, usually the × button, and
        // iOS Safari draws a focus ring on it. Start on the dialog itself
        // instead; Tab then reaches the first control (#103).
        if (!dialog.querySelector('[autofocus]')) dialog.focus();
      }
      return;
    }
    if (!dialog.open || dialog.dataset.closing !== undefined) return;
    dialog.dataset.closing = '';
    stepBack(false);
    // The page steps forward again with the sheet; release the scroll lock
    // only when both are done, or the page jumps (#162).
    void Promise.all([
      afterAnimations(dialog),
      afterAnimations(document.getElementById('root')),
    ]).then(() => {
      // Opened again while the exit animation was running.
      if (dialog.dataset.closing === undefined) return;
      delete dialog.dataset.closing;
      dialog.close();
      lock(false);
    });
  }, [open]);

  // Layout cleanup runs while the element is still in the document.
  useLayoutEffect(() => {
    const dialog = ref.current;
    return () => {
      stepBack(false);
      lock(false);
      if (!dialog?.open) return;
      const ghost = dialog.cloneNode(true) as HTMLDialogElement;
      const focusBack = opener.current;
      queueMicrotask(() => {
        // Still connected: StrictMode re-ran the effect, nothing was unmounted.
        if (dialog.isConnected) return;
        const restoreFocus = () => {
          if (focusBack instanceof HTMLElement && focusBack.isConnected) focusBack.focus();
        };
        if (typeof ghost.getAnimations !== 'function') {
          restoreFocus();
          return;
        }
        ghost.removeAttribute('open');
        ghost.inert = true;
        ghost.dataset.closing = '';
        document.body.append(ghost);
        ghost.showModal();
        // The page is inert while the copy is modal, so focus returns afterwards.
        void afterAnimations(ghost).then(() => {
          ghost.remove();
          restoreFocus();
        });
      });
    };
  }, []);

  return ref;
}

/** Resolves once all running animations on the element have ended or were cancelled. */
function afterAnimations(element: HTMLElement | null): Promise<unknown> {
  if (!element) return Promise.resolve();
  const animations = typeof element.getAnimations === 'function' ? element.getAnimations() : [];
  return Promise.allSettled(animations.map((animation) => animation.finished));
}
